import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, ApiError } from "@/lib/apiClient";
import { tokyoLocalToIso, todayTokyoDateInput, formatDate } from "@/lib/date";
import { validateReservationForm } from "@/lib/validators";
import type { PcAvailability } from "@/types/pc";
import type { User, CurrentUser } from "@/types/user";
import type { Reservation } from "@/types/reservation";
import { LoadingState } from "@/components/LoadingState";
import { useToast } from "@/hooks/useToast";

const DEFAULT_START_TIME = "09:00";
const DEFAULT_END_TIME = "18:00";

export function ReservationCreatePage({ currentUser }: { currentUser: CurrentUser }) {
  const navigate = useNavigate();
  const notify = useToast();
  const canProxy = currentUser.role === "admin";

  const [startDate, setStartDate] = useState(todayTokyoDateInput());
  const [endDate, setEndDate] = useState(todayTokyoDateInput());
  const [pcId, setPcId] = useState<number | null>(null);
  const [userId, setUserId] = useState<number | null>(canProxy ? null : currentUser.id);
  const [purpose, setPurpose] = useState("");
  const [notes, setNotes] = useState("");

  const [users, setUsers] = useState<User[]>([]);
  const [availability, setAvailability] = useState<PcAvailability[]>([]);
  const [loadingAvailability, setLoadingAvailability] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (canProxy) {
      api.get<User[]>("/users").then(setUsers).catch(() => undefined);
    }
  }, [canProxy]);

  const startIso = startDate ? tokyoLocalToIso(startDate, DEFAULT_START_TIME) : null;
  const endIso = endDate ? tokyoLocalToIso(endDate, DEFAULT_END_TIME) : null;

  useEffect(() => {
    if (!startIso || !endIso) return;
    if (new Date(startIso).getTime() >= new Date(endIso).getTime()) return;

    setLoadingAvailability(true);
    api
      .get<PcAvailability[]>(
        `/pcs/available?startAt=${encodeURIComponent(startIso)}&endAt=${encodeURIComponent(endIso)}`
      )
      .then((list) => {
        setAvailability(list);
        if (pcId && !list.find((a) => a.pc.id === pcId && a.available)) {
          setPcId(null);
        }
      })
      .catch(() => setAvailability([]))
      .finally(() => setLoadingAvailability(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [startIso, endIso]);

  const submit = async () => {
    if (submitting || !startIso || !endIso) return;

    const validationError = validateReservationForm(
      { pcId, userId, startDate, endDate, purpose, notes },
      startIso,
      endIso
    );
    if (validationError) {
      setError(validationError);
      return;
    }

    setSubmitting(true);
    setError(null);
    try {
      const reservation = await api.post<Reservation>("/reservations", {
        pcId,
        userId: canProxy ? userId : undefined,
        startAt: startIso,
        endAt: endIso,
        purpose: purpose || null,
        notes: notes || null,
      });
      notify("利用申請を送信しました。");
      navigate(`/reservations/${reservation.id}`);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "申請に失敗しました。");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div>
      <h1 className="page-title">利用申請</h1>
      <p className="page-subtitle">PCの利用予約を申請します。</p>

      <div className="card card-padded" style={{ maxWidth: 560 }}>
        {canProxy && (
          <div className="form-field">
            <label>使用者</label>
            <select value={userId ?? ""} onChange={(e) => setUserId(e.target.value ? Number(e.target.value) : null)}>
              <option value="">選択してください</option>
              {users
                .filter((u) => u.active)
                .map((u) => (
                  <option key={u.id} value={u.id}>
                    {u.displayName}
                  </option>
                ))}
            </select>
          </div>
        )}
        {!canProxy && (
          <div className="form-field">
            <label>使用者</label>
            <input value={currentUser.displayName} disabled />
          </div>
        )}

        <div className="form-row">
          <div className="form-field">
            <label>利用開始日</label>
            <input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} />
          </div>
          <div className="form-field">
            <label>利用終了日</label>
            <input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} />
          </div>
        </div>

        <div className="form-field">
          <label>使用PC</label>
          {loadingAvailability ? (
            <LoadingState label="空きPCを確認中..." />
          ) : (
            <select value={pcId ?? ""} onChange={(e) => setPcId(e.target.value ? Number(e.target.value) : null)}>
              <option value="">選択してください</option>
              {availability.map((a) => (
                <option key={a.pc.id} value={a.pc.id} disabled={!a.available}>
                  {a.pc.name}
                  {!a.available
                    ? a.blockingReservation
                      ? `　予約済み（${a.blockingReservation.userDisplayName} ${formatDate(
                          a.blockingReservation.startAt
                        )}〜${formatDate(a.blockingReservation.endAt)}）`
                      : "　利用不可"
                    : ""}
                </option>
              ))}
            </select>
          )}
        </div>

        <div className="form-field">
          <label>利用目的</label>
          <textarea value={purpose} onChange={(e) => setPurpose(e.target.value)} maxLength={200} placeholder="例：画像編集作業" />
        </div>

        <div className="form-field">
          <label>備考</label>
          <textarea value={notes} onChange={(e) => setNotes(e.target.value)} maxLength={1000} />
        </div>

        {error && <p className="error-text">{error}</p>}

        <button className="btn btn-primary btn-block" onClick={submit} disabled={submitting}>
          {submitting ? "送信中..." : "申請する"}
        </button>
      </div>
    </div>
  );
}
